// components/SheetFlexGrid/SheetFlexGrid.jsx
import React, { useEffect, useMemo, useRef } from "react";
import "./SheetFlexGrid.css";
import { useLanguage } from "../../LanguageProvider";

export default function SheetFlexGrid({
  rows,
  onChange,
  columns,
  includeIndex = true,
  includeDelete = true,
  onDeleteRow,
  statusKey,
  statusLabels = {},
  statusClasses = {},
  footerLeft,
  legend = [],
  pasteEnabled = true,
  pasteMap = [],
  resolveOption,
  extraCellClass,
  showRequiredErrors = true,
  // ✅ Hücre odağı bildirimi (görseli bozmaz)
  onCellFocus,
}) {
  const wrapperRef = useRef(null);
  const { getTranslation } = useLanguage();

  const headers = useMemo(() => {
    const base = [];
    if (includeIndex) base.push({ key: "__#__", title: "#" });
    base.push(...columns.map((c) => ({ key: c.key, title: c.title })));
    if (statusKey) base.push({ key: "__status__", title: getTranslation("status") });
    if (includeDelete) base.push({ key: "__delete__", title: getTranslation("delete") });
    return base;
  }, [columns, includeIndex, includeDelete, statusKey]);

  const setCell = (idx, key, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };

  const handleDelete = (idx) => {
    if (onDeleteRow) return onDeleteRow(idx);
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
  };

  // ---- PASTE (opsiyonel; üst bileşen yönetiyorsa kapatılabilir) ----
  const parsePastedText = (text) => {
    const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
      .split("\n").filter((l) => l.trim().length > 0);
    const out = [];
    for (const line of lines) {
      const parts = (line.includes("\t") ? line.split("\t") : line.split(",")).map((x) => x.trim());
      const rowObj = {};
      pasteMap.forEach((k, i) => {
        const raw = parts[i] ?? "";
        const col = columns.find((c) => c.key === k);
        if (col?.type === "select" && resolveOption) {
          rowObj[k] = resolveOption(col, raw);
        } else if (col?.type === "checkbox") {
          rowObj[k] = /^true|1|evet|yes$/i.test(raw);
        } else {
          rowObj[k] = raw;
        }
      });
      out.push(rowObj);
    }
    return out;
  };

  const onPaste = (e) => {
    if (!pasteEnabled) return;
    if (!e.clipboardData) return;
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    if (!pasteMap?.length) return;
    const parsed = parsePastedText(text);
    if (parsed.length) {
      const isFirstEmpty =
        rows.length === 1 &&
        Object.values(rows[0] || {}).every((v) => v === "" || v === null || v === undefined || v === false);
      const next = isFirstEmpty ? parsed : [...rows, ...parsed];
      onChange(next);
    }
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !pasteEnabled) return;
    el.addEventListener("paste", onPaste);
    return () => el.removeEventListener("paste", onPaste);
  }, [rows, pasteEnabled, columns, pasteMap]);

  // küçük yardımcı: handler'ları zincirle (görsel dokunmadan)
  const chain =
    (...fns) =>
      (...args) =>
        fns.forEach((fn) => {
          try { fn && fn(...args); } catch { }
        });

  return (
    <div className="excel-like-table-wrapper" ref={wrapperRef}>
      <table className="excel-like-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h.key}>{h.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className={idx % 2 ? "odd" : ""}>
              {includeIndex && <td className="row-index">{idx + 1}</td>}

              {columns.map((col) => {
                const val = row[col.key];

                // 🔎 Validasyon sınıfları (input/select üzerinde kalır)
                const validatedInvalid = col.validate ? !col.validate(val, row) : false;
                const requiredInvalid = col.required ? !String(val ?? "").trim() : false;
                const showRequiredInvalid = showRequiredErrors && requiredInvalid;

                const title = typeof col.tooltip === "function" ? (col.tooltip(val, row) || "") : "";
                const extra = extraCellClass ? extraCellClass(row, col, val) : "";

                // ⬇️ Stil bozulmasın diye sınıflar yine INPUT/SELECT üzerinde
                const ioClassName = [
                  col.type === "select" ? "cell-select" : "cell-input",
                  showRequiredInvalid ? "invalid-required" : "",
                  validatedInvalid ? "invalid-validated" : "",
                  extra,
                ].join(" ").trim();

                // TD'ye sadece data-colkey ve varsa cellProps (className eklemek istemiyorsan verme)
                const tdProps = {
                  ...(col.cellProps || {}),
                  "data-colkey":
                    (col.cellProps && (col.cellProps["data-colkey"] || col.cellProps["data-colKey"] || col.cellProps["data-col-key"])) ||
                    col.key, // anchor için
                };

                // INPUT/SELECT'e ekstra props (görseli bozmaz)
                const extraProps = col.extraProps || {};
                const onFocusChained = chain(extraProps.onFocus, () => onCellFocus && onCellFocus(idx, col.key));
                const onClickChained = chain(extraProps.onClick, () => onCellFocus && onCellFocus(idx, col.key));
                const onPasteCaptureChained = chain(extraProps.onPasteCapture);

                const commonIOProps = {
                  title,
                  autoComplete: extraProps.autoComplete || "new-password",
                  name: extraProps.name,
                  "data-colkey": extraProps["data-colkey"] || extraProps["data-colKey"] || extraProps["data-col-key"],
                  "aria-label": extraProps["aria-label"],
                  onFocus: onFocusChained,
                  onClick: onClickChained,
                  onPasteCapture: onPasteCaptureChained,
                  className: ioClassName,
                  autoCorrect: "off",
                  spellCheck: false
                };

                return (
                  <td key={col.key} {...tdProps}>
                    {col.type === "select" ? (
                      <select
                        {...commonIOProps}
                        value={val ?? ""}
                        onChange={(e) => setCell(idx, col.key, e.target.value)}
                      >
                        <option value="">{col.placeholder || "Seçiniz"}</option>
                        {(col.options || []).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : col.type === "checkbox" ? (
                      <div className="checkbox-cell">
                        <input
                          {...commonIOProps}
                          type="checkbox"
                          checked={!!val}
                          onChange={(e) => setCell(idx, col.key, e.target.checked)}
                        />
                      </div>
                    ) : (
                      <input
                        {...commonIOProps}
                        value={val ?? ""}
                        placeholder={col.placeholder || ""}
                        onChange={(e) => setCell(idx, col.key, e.target.value)}
                      />
                    )}
                  </td>
                );
              })}

              {statusKey && (
                <td className="status-cell">
                  {row[statusKey] ? (
                    <span className={`status ${statusClasses[row[statusKey]] || ""}`}>
                      {statusLabels[row[statusKey]] || row[statusKey]}
                    </span>
                  ) : null}
                </td>
              )}

              {includeDelete && (
                <td className="actions-cell">
                  <button className="row-delete" onClick={() => handleDelete(idx)} title="Satırı sil">
                    ×
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {(footerLeft || (legend && legend.length)) && (
        <div className="table-footer">
          <div>{footerLeft}</div>
          {legend && legend.length ? (
            <div className="legend">
              {legend.map((it, i) => (
                <span key={i}>
                  <span className={`dot ${it.dotClass}`} /> {it.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}